import clsx from "clsx"

import {
  useArticlesByAuthorQuery,
  useAuthorBioQuery,
} from "~/api/prismic/graphql"
import { ChunkyUnderline } from "~/components/ChunkyUnderline"
import CustomRichText from "~/components/CustomRichText"
import type { LinkProps } from "~/components/Link"
import { Link } from "~/components/Link"
import { Loading } from "~/components/Loading"
import { InstagramLink, TwitterLink } from "~/components/SocialIconLink"
import { getSummaryFromGQLArticle } from "~/utils/articles/getSummaryText"
import { getListQueryNodes } from "~/utils/graphql"
import { getArticleLink } from "~/utils/links/getArticleLink"
import {
  getImageDetails,
  getRichTextBlocks,
} from "~/utils/prismic/assertJsonTypes"
import { asText } from "~/utils/prismic/richText"
import { withoutFalsyValues } from "~/utils/withoutFalsyValues"

import type { ArticleGalleryProps } from "./ArticleGallery"
import { ArticleGallery } from "./ArticleGallery"

export type AuthorDetailsProps = {
  id: string
  uid: string
  name: string
  contacts: {
    twitter: string | null
    instagram: string | null
  }
  connection: {
    name: string
    href: LinkProps["href"]
  } | null
  isSelected: boolean
}

export const AuthorDetails = ({
  id,
  uid,
  name,
  contacts,
  connection,
  isSelected,
}: AuthorDetailsProps): JSX.Element | null => {
  const { data: bio, isLoading: isBioLoading } = useAuthorBioQuery(
    { uid },
    {
      enabled: isSelected,
      select: (data) => getRichTextBlocks(data.author?.bio),
      staleTime: Infinity,
    },
  )

  const { data: articles } = useArticlesByAuthorQuery(
    { authorID: id },
    {
      enabled: isSelected,
      select: (data): ArticleGalleryProps["articles"] => {
        const articleNodes = getListQueryNodes(data.allArticles)
        const previews = articleNodes.map((article) => {
          const { uid: articleUid } = article._meta
          if (!articleUid) return null

          return {
            title: asText(getRichTextBlocks(article.title)),
            description: getSummaryFromGQLArticle(article),
            image: getImageDetails(article.banner_image),
            date: article._meta.firstPublicationDate,
            href: getArticleLink({ uid: articleUid }),
          }
        })

        return withoutFalsyValues(previews)
      },
      staleTime: Infinity,
    },
  )

  return (
    <div
      className={clsx(
        "flex flex-col md:flex-row",
        "space-y-4 p-4 md:space-y-0 md:space-x-4",
        "bg-primary-50 dark:bg-primary-700",
      )}
    >
      <div className="prose basis-1/2 dark:prose-dark">
        <h3 className="hidden md:inline-block">
          <ChunkyUnderline>{name}</ChunkyUnderline>
        </h3>

        <div className="flex space-x-2">
          {!!contacts.twitter && (
            <TwitterLink iconSize={20} username={contacts.twitter} />
          )}
          {!!contacts.instagram && (
            <InstagramLink iconSize={20} username={contacts.instagram} />
          )}
        </div>

        {connection && (
          <p className="text-sm italic">
            <Link href={connection.href} shallow>
              {/* TODO: Move this to translation files */}
              <span>A convite de </span>
              <ChunkyUnderline>{connection.name}</ChunkyUnderline>
            </Link>
          </p>
        )}

        {isBioLoading && <Loading />}
        {!!bio && <CustomRichText field={bio} />}
      </div>

      <div className="basis-1/2">
        {isSelected && articles && <ArticleGallery articles={articles} />}
      </div>
    </div>
  )
}
